import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Trophy,
  Users,
  Plane,
  MapPin,
  Newspaper,
  LayoutDashboard,
  Sun,
  Moon,
  Menu,
  X,
  ChevronDown,
  Search,
  Ticket,
  User,
  MoreHorizontal,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const hubNav = [
  { path: '/', label: 'Home', match: (p) => p === '/' },
  { path: '/matches', label: 'Matches', match: (p) => p === '/matches' || p.startsWith('/matches/') },
  { path: '/stadiums', label: 'Stadiums', match: (p) => p === '/stadiums' || p.startsWith('/stadiums/') },
  { path: '/guides', label: 'Guides', match: (p) => p === '/guides' || p.startsWith('/guides/') },
  { path: '/itinerary-planner', label: 'Planner', match: (p) => p.startsWith('/itinerary-planner') },
  { path: '/fan-meetups', label: 'Community', match: (p) => p.startsWith('/fan-meetups') },
];

const moreNavItems = [
  { path: '/teams', label: 'Teams', icon: Users },
  { path: '/cities', label: 'Cities', icon: MapPin },
  { path: '/travel-planner', label: 'Travel planner', icon: Plane },
  { path: '/news', label: 'News', icon: Newspaper },
  { path: '/standings', label: 'Standings', icon: Trophy },
  { path: '/cockpit', label: 'Cockpit', icon: LayoutDashboard },
];

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreMobileOpen, setMoreMobileOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const moreRef = useRef(null);

  const isMoreActive = moreNavItems.some((item) => location.pathname === item.path);

  useEffect(() => {
    if (!moreOpen) return;
    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [moreOpen]);

  const hubLinkClass = (isActive) =>
    isActive
      ? 'text-primary dark:text-primary-container border-b-4 border-tertiary pb-1 font-bold text-sm whitespace-nowrap'
      : 'text-on-surface-variant dark:text-slate-400 font-medium hover:text-secondary dark:hover:text-blue-400 transition-colors text-sm whitespace-nowrap';

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    if (q) navigate(`/stadiums?q=${encodeURIComponent(q)}`);
    else navigate('/stadiums');
    setMobileOpen(false);
  };

  return (
    <header className="glass-nav bg-surface/70 dark:bg-slate-900/80 backdrop-blur-xl border-b border-outline-variant/10 dark:border-slate-800 sticky top-0 z-50 transition-colors">
      <nav className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main">
        <div className="flex justify-between items-center h-16 md:h-[4.25rem]">
          <Link
            to="/"
            className="text-xl sm:text-2xl font-black italic tracking-tighter text-primary dark:text-primary-container shrink-0"
          >
            Soccer Hub
          </Link>

          <div className="hidden md:flex items-center gap-6 lg:gap-8 flex-1 justify-center px-4">
            {hubNav.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={hubLinkClass(item.match(location.pathname))}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <form
              onSubmit={handleSearchSubmit}
              className="hidden lg:flex items-center bg-surface-container-low dark:bg-slate-800/80 px-3 py-2 rounded-full border-0 min-w-0 max-w-[200px] xl:max-w-[240px]"
            >
              <Search className="h-4 w-4 text-outline shrink-0 mr-2" aria-hidden />
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search cities..."
                className="bg-transparent border-none focus:ring-0 text-sm w-full min-w-0 text-on-surface dark:text-slate-200 placeholder:text-on-surface-variant dark:placeholder:text-slate-500"
                aria-label="Search cities and stadiums"
              />
            </form>

            <Link
              to="/tickets"
              className="hidden sm:inline-flex items-center bg-primary text-on-primary px-4 py-2 rounded-full font-bold text-sm shadow-sm hover:brightness-110 active:scale-95 transition-all"
            >
              Tickets
            </Link>

            <Link
              to="/cockpit"
              className="hidden md:inline-flex p-2 rounded-full text-on-surface-variant hover:text-primary dark:text-slate-400 dark:hover:text-primary-container transition-colors"
              aria-label="Fan cockpit dashboard"
            >
              <User className="h-5 w-5" />
            </Link>

            <div className="hidden lg:block relative" ref={moreRef}>
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(!moreOpen);
                }}
                className={`flex items-center px-2 py-2 rounded-xl text-sm font-medium transition-colors ${
                  isMoreActive ? 'text-primary bg-surface-container-low dark:bg-slate-800' : 'text-on-surface-variant hover:bg-surface-container-low dark:hover:bg-slate-800'
                }`}
                aria-expanded={moreOpen}
                aria-haspopup="true"
                id="more-trigger"
              >
                <MoreHorizontal className="h-5 w-5" aria-hidden />
                <span className="sr-only">More navigation</span>
              </button>
              {moreOpen && (
                <div
                  role="menu"
                  aria-labelledby="more-trigger"
                  className="absolute top-full right-0 mt-1 py-1 min-w-[200px] rounded-xl bg-surface-container-lowest dark:bg-slate-800 border border-outline-variant/15 dark:border-slate-700 shadow-fanhub z-50"
                >
                  {moreNavItems.map((item) => {
                    const Icon = item.icon;
                    const active = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        role="menuitem"
                        onClick={() => setMoreOpen(false)}
                        className={`flex items-center px-3 py-2 text-sm font-medium mx-1 rounded-lg transition-colors ${
                          active ? 'bg-primary/10 text-primary' : 'text-on-surface dark:text-slate-200 hover:bg-surface-container-low dark:hover:bg-slate-700/80'
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-2 shrink-0" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              className="p-2.5 rounded-xl text-on-surface-variant hover:text-primary hover:bg-surface-container-low dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-primary-container transition-colors shrink-0"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            <div className="flex md:hidden items-center gap-1">
              <Link
                to="/tickets"
                className="inline-flex items-center justify-center p-2 rounded-full text-primary"
                aria-label="Tickets"
              >
                <Ticket className="h-5 w-5" />
              </Link>
              <button
                type="button"
                onClick={() => setMobileOpen(!mobileOpen)}
                className="p-2.5 rounded-xl text-on-surface dark:text-slate-200 hover:bg-surface-container-low dark:hover:bg-slate-800 transition-colors"
                aria-expanded={mobileOpen}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-outline-variant/15 dark:border-slate-800 overflow-y-auto max-h-[calc(100vh-4rem)]">
            <form onSubmit={handleSearchSubmit} className="flex items-center bg-surface-container-low dark:bg-slate-800 rounded-full px-3 py-2 mb-4">
              <Search className="h-4 w-4 text-outline mr-2 shrink-0" />
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search cities..."
                className="bg-transparent border-none focus:ring-0 text-sm flex-1 text-on-surface dark:text-slate-200"
              />
            </form>
            <div className="flex flex-col gap-1">
              {hubNav.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`px-3 py-3 rounded-xl text-sm font-medium ${
                    item.match(location.pathname)
                      ? 'bg-primary/10 text-primary font-bold'
                      : 'text-on-surface dark:text-slate-200 hover:bg-surface-container-low dark:hover:bg-slate-800'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/tickets"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-3 rounded-xl text-sm font-bold bg-primary text-on-primary text-center mt-2"
              >
                Tickets
              </Link>
            </div>
            <button
              type="button"
              onClick={() => setMoreMobileOpen(!moreMobileOpen)}
              className="flex items-center justify-between w-full px-3 py-2 mt-3 text-sm font-medium text-on-surface dark:text-slate-200"
            >
              More
              <ChevronDown className={`h-4 w-4 transition-transform ${moreMobileOpen ? 'rotate-180' : ''}`} />
            </button>
            {moreMobileOpen && (
              <div className="pl-2 space-y-1 mb-2">
                {moreNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center px-3 py-2 rounded-lg text-sm text-on-surface dark:text-slate-200 hover:bg-surface-container-low dark:hover:bg-slate-800"
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
